import { TaskStatus, BasicMonitorStatus, BatchResultStatus } from '@/services/enums';
/**
 * 量化策略服务
 */

import { apiClient } from './apiClient';

export interface BatchBacktestStatus {
  taskId: string;
  strategyCode: string;
  strategyName: string;
  status: TaskStatus;
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  currentTask: { symbol: string; instType: string } | null;
  results: Array<{
    symbol: string;
    instType: string;
    status: BatchResultStatus;
    backtestId?: number;
    error?: string;
  }>;
  startedAt: string;
  completedAt: string | null;
}

export interface BatchOptimizeStatus {
  taskId: string;
  strategyCode: string;
  status: TaskStatus;
  currentGeneration?: number;
  maxGenerations?: number;
  bestScore?: number | null;
  bestParams?: Record<string, any>;
  error?: string;
  startedAt: string;
  completedAt: string | null;
}

export interface StrategyDetailData {
  strategy: {
    code: string;
    name: string;
    description: string;
    category: string;
    defaultParams: Record<string, any>;
    paramDescriptions?: Record<string, string>;
    optimizedParamsMap: Record<string, Record<string, any>>;
  };
  backtests: Array<{
    id: number;
    symbol: string;
    instType: string;
    timeframe: string;
    totalReturnPct: number | null;
    winRate: number | null;
    sharpeRatio: number | null;
    maxDrawdown: number | null;
    totalTrades: number | null;
    startTime: string;
    endTime: string;
    createdAt: string;
  }>;
  averageBacktest: {
    totalReturnPct: number;
    winRate: number;
    sharpeRatio: number;
    maxDrawdown: number;
    totalTrades: number;
  } | null;
  totalBacktests: number;
  validBacktests: number;
}

export interface StrategyListItem {
  id: number;
  strategyCode: string;
  strategyName: string;
  symbol: string;
  instType: string;
  status: BasicMonitorStatus;
  createdAt: string;
  [key: string]: any;
}

export interface OptimizeStatus {
  taskId: string;
  strategyCode: string;
  status: TaskStatus;
  currentIteration?: number;
  maxIterations?: number;
  bestScore?: number | null;
  bestParams?: Record<string, any>;
  error?: string;
  startedAt: string;
  completedAt: string | null;
}

export class QuantitativeStrategyService {
  /**
   * 获取策略详情
   */
  async getStrategyDetail(strategyCode: string): Promise<StrategyDetailData> {
    const response = await apiClient.get<StrategyDetailData>(
      `/quantitative-strategies/${strategyCode}/detail`,
    );

    if (!response.success) {
      throw new Error(response.message || '获取策略详情失败');
    }

    return response.data;
  }

  /**
   * 启动批量回测
   */
  async startBatchBacktest(
    strategyCode: string,
    params: {
      timeframe: string;
      startTime: string;
      endTime: string;
      concurrency?: number;
    },
  ): Promise<{ taskId: string }> {
    const response = await apiClient.post<{ taskId: string }>(
      `/quantitative-strategies/${strategyCode}/batch-backtest`,
      params,
    );

    if (!response.success) {
      throw new Error(response.message || '启动批量回测失败');
    }

    return response.data;
  }

  /**
   * 获取批量回测状态
   */
  async getBatchBacktestStatus(taskId: string): Promise<BatchBacktestStatus> {
    const response = await apiClient.get<BatchBacktestStatus>(
      `/quantitative-strategies/batch-backtest/${taskId}`,
    );

    if (!response.success) {
      throw new Error(response.message || '获取批量回测状态失败');
    }

    return response.data;
  }

  /**
   * 启动批量优化
   */
  async startBatchOptimize(
    strategyCode: string,
    params: {
      timeframe: string;
      startTime: string;
      endTime: string;
      populationSize?: number;
      maxGenerations?: number;
    },
  ): Promise<{ taskId: string }> {
    const response = await apiClient.post<{ taskId: string }>(
      `/quantitative-strategies/${strategyCode}/batch-optimize-all`,
      params,
    );

    if (!response.success) {
      throw new Error(response.message || '启动批量优化失败');
    }

    return response.data;
  }

  /**
   * 获取批量优化状态
   */
  async getBatchOptimizeStatus(taskId: string): Promise<BatchOptimizeStatus> {
    const response = await apiClient.get<BatchOptimizeStatus>(
      `/quantitative-strategies/batch-optimize-all/${taskId}`,
    );

    if (!response.success) {
      throw new Error(response.message || '获取批量优化状态失败');
    }

    return response.data;
  }

  /**
   * 获取所有策略列表
   */
  async getStrategies(): Promise<StrategyListItem[]> {
    const response = await apiClient.get<StrategyListItem[]>('/quantitative-strategies');

    if (!response.success) {
      throw new Error(response.message || '获取策略列表失败');
    }

    return response.data || [];
  }

  /**
   * 启动策略
   */
  async startStrategy(id: number): Promise<any> {
    const response = await apiClient.post<any>(`/quantitative-strategies/${id}/start`);

    if (!response.success) {
      throw new Error(response.message || '启动策略失败');
    }

    return response.data;
  }

  /**
   * 停止策略
   */
  async stopStrategy(id: number): Promise<any> {
    const response = await apiClient.post<any>(`/quantitative-strategies/${id}/stop`);

    if (!response.success) {
      throw new Error(response.message || '停止策略失败');
    }

    return response.data;
  }

  /**
   * 删除策略实例
   */
  async deleteStrategy(id: number): Promise<any> {
    const response = await apiClient.delete<any>(`/quantitative-strategies/${id}`);

    if (!response.success) {
      throw new Error(response.message || '删除策略失败');
    }

    return response.data;
  }

  /**
   * 启动单策略参数优化（旧接口，已废弃，使用 batch-optimize-all）
   */
  async startOptimize(
    strategyCode: string,
    params: {
      timeframe: string;
      method?: string;
      objective?: string;
      maxIterations?: number;
      concurrency?: number;
    },
  ): Promise<{ taskId: string }> {
    const response = await apiClient.post<{ taskId: string }>(
      `/quantitative-strategies/${strategyCode}/batch-optimize`,
      params,
    );

    if (!response.success) {
      throw new Error(response.message || '启动参数优化失败');
    }

    return response.data;
  }

  /**
   * 获取优化状态
   */
  async getOptimizeStatus(taskId: string): Promise<OptimizeStatus> {
    const response = await apiClient.get<OptimizeStatus>(
      `/quantitative-strategies/batch-optimize/${taskId}`,
    );

    if (!response.success) {
      throw new Error(response.message || '获取优化状态失败');
    }

    return response.data;
  }
}

export const quantitativeStrategyService = new QuantitativeStrategyService();
